import {
  type SubmissionResult,
  getFormProps,
  useForm,
} from "@conform-to/react";
import { CloseButton } from "@headlessui/react";
import { Trans, t } from "@lingui/macro";
import { AlertActions } from "@winamp/ui/components/Alert/AlertActions.js";
import { AlertDescription } from "@winamp/ui/components/Alert/AlertDescription.js";
import { AlertTitle } from "@winamp/ui/components/Alert/AlertTitle.js";
import { AlertWrapper } from "@winamp/ui/components/Alert/AlertWrapper.js";
import { Button } from "@winamp/ui/components/Button/Button.js";
import { FormErrorsMessage } from "@winamp/ui/components/Fieldset/FormErrorsMessage.js";
import { getValibotConstraint, parseWithValibot } from "conform-to-valibot";
import { useEffect, useState } from "react";
import { useFetcher } from "react-router";
import { $path } from "safe-routes";
import * as v from "valibot";
import type { Permission } from "~/models/permission";
import { Retryable } from "~/modules/retry/Retryable";
import type { SerializeFrom } from "~/modules/routing/types";
import {
  type action as LabelCollaboratorRolesAction,
  type loader as LabelCollaboratorRolesLoader,
  getLabelCollaboratorRolesSchema,
} from "~/routes/account+/admin+/collaborators+/$id+/labels+/$labelId+/roles";
import {
  type DialogProps,
  DialogRetryPlaceholder,
  useDialogDataFetcher,
} from "..";
import { useRouteDialogParams } from "../../RouteDialogsContext";

export const key = "label.collaborator.roles.revoke";

export const permissions: Array<Permission | RegExp> = [
  "Admin.DeleteCollaborator",
];

export const Schema = () =>
  v.object({
    labelUid: v.pipe(
      v.string(t`The label ID is required.`),
      v.minLength(1, t`The label ID is required.`),
    ),
    userUid: v.pipe(
      v.string(t`The user ID is required.`),
      v.minLength(1, t`The user ID is required.`),
    ),
  });

export default function View({ hashParams, ...props }: DialogProps) {
  const params = useRouteDialogParams(Schema(), hashParams, props.open);

  const userUid = params?.userUid;
  const labelUid = params?.labelUid;

  const {
    data,
    isLoading: isFetcherLoading,
    load,
    state,
  } = useDialogDataFetcher<typeof LabelCollaboratorRolesLoader>(
    userUid &&
      labelUid &&
      $path("/account/admin/collaborators/:id/labels/:labelId/roles", {
        id: userUid,
        labelId: labelUid,
      }),
    {
      key,
      deps: [props.open, userUid, labelUid],
      when: props.open && !!userUid && !!labelUid,
    },
  );

  const isLoading =
    isFetcherLoading ||
    (!!data && (data.user?.uid !== userUid || data.label?.uid !== labelUid));

  return (
    <ViewBody
      {...props}
      data={data}
      hashParams={hashParams}
      loading={isLoading}
      loadData={() => load()}
      reloading={state !== "idle"}
    />
  );
}

export function ViewBody({
  data,
  hashParams,
  loadData,
  reloading,
  ...props
}: {
  data?: SerializeFrom<typeof LabelCollaboratorRolesLoader>;
} & DialogProps<true>) {
  const [lastResult, setLastResult] = useState<
    SubmissionResult<Array<string>> | null | undefined
  >(null);

  const formAction =
    data?.user && data?.label
      ? $path("/account/admin/collaborators/:id/labels/:labelId/roles", {
          id: data.user.uid,
          labelId: data.label.uid,
        })
      : "";
  const fetcher = useFetcher<typeof LabelCollaboratorRolesAction>({
    key: `${key}-action`,
  });
  const isPending = fetcher.state !== "idle";

  useEffect(() => {
    setLastResult(fetcher.data);

    // Close after successful actions
    if (fetcher.data?.status === "success") {
      closeImmediately();
    }
  }, [fetcher.data]);

  const LabelCollaboratorRolesSchema = getLabelCollaboratorRolesSchema();
  const [form] = useForm({
    lastResult,
    constraint: getValibotConstraint(LabelCollaboratorRolesSchema),
    id: [key, data?.user?.uid, data?.label?.uid, props.open].join("-"),
    defaultValue: {
      roleIds: [],
    },
    onValidate({ formData }) {
      const validation = parseWithValibot(formData, {
        schema: LabelCollaboratorRolesSchema,
      });

      return validation;
    },
  });

  const close = () => {
    if (!isPending) {
      closeImmediately();
    }
  };

  const closeImmediately = () => {
    props.onClose?.(false);
    setLastResult({ initialValue: null });
  };

  return (
    <AlertWrapper {...props} onClose={close}>
      <Retryable
        condition={!!data?.user && !!data?.label && !!data.roles}
        fallback={({ shouldRetry, ...retryProps }) => (
          <>
            <AlertTitle>
              <Trans>Revoke access for a user</Trans>
            </AlertTitle>
            <AlertDescription>
              <Trans>
                Are you sure you want to revoke this collaborator's access for
                this label?
              </Trans>
              <br />
              <Trans>
                Once you revoke the collaborator's access, they will no longer
                be able to access this label's account.
              </Trans>
            </AlertDescription>
            <DialogRetryPlaceholder dialogKey={key} {...retryProps} />
          </>
        )}
        onRetry={() => loadData?.()}
        isRetrying={reloading}
      >
        <AlertTitle>
          <Trans>
            Revoke access for{" "}
            {data?.user?.displayName ??
              [data?.user?.firstName, data?.user?.lastName]
                .filter(Boolean)
                .join(" ")}
          </Trans>
        </AlertTitle>
        <AlertDescription>
          <Trans>
            Are you sure you want to revoke this collaborator's access for this
            label?
          </Trans>
          <br />
          <Trans>
            Once you revoke the collaborator's access, they will no longer be
            able to access this label's account.
          </Trans>
        </AlertDescription>
        <fetcher.Form
          {...getFormProps(form)}
          method="post"
          action={formAction}
        />
        <FormErrorsMessage errors={form.errors} />
        <AlertActions>
          <CloseButton as={Button} disabled={isPending} variant="ghost">
            <Trans>Cancel</Trans>
          </CloseButton>
          <Button
            form={form.id}
            type="submit"
            color="red"
            autoFocus
            loading={isPending}
          >
            <Trans>Yes, revoke</Trans>
          </Button>
        </AlertActions>
      </Retryable>
    </AlertWrapper>
  );
}
